import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../../components/Layout';
import FuelPriceTable from '../../components/FuelPriceTable';
import SEOContent from '../../components/SEOContent';
import useTheme from '../../hooks/useTheme';
import useExchangeRate from '../../hooks/useExchangeRate';

const GallonsToLitersEN = () => {
  const { darkMode, toggleDarkMode, cardClass, inputClass } = useTheme();
  const { dollarRate } = useExchangeRate();

  const [gallons, setGallons] = useState('');
  const [result, setResult] = useState(null);

  const handleConvert = (value) => {
    setGallons(value);
    if (value && parseFloat(value) > 0) {
      const gallonsNum = parseFloat(value);
      const liters = gallonsNum * 3.78541;
      setResult({
        gallons: gallonsNum,
        liters: liters.toFixed(2),
        litersRounded: Math.round(liters * 10) / 10
      });
    } else {
      setResult(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Gallons to Liters Converter - Free Calculator | Gallons&Liters</title>
        <meta name="description" content="Convert US gallons to liters instantly. Free and accurate gallons to liters calculator with US fuel price table." />
        <meta name="keywords" content="gallons to liters, gallon to liter converter, gallons liters calculator, how many liters in a gallon" />
        <link rel="canonical" href="https://galonyilitry.pl/en/gallons-to-liters-converter" />
        <html lang="en" />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="en">
        <div className="max-w-4xl mx-auto pt-20 pb-12 px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-4">
              Gallons to Liters Converter
            </h1>
            <p className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Quickly convert US gallons to liters. 1 gallon = 3.78541 liters
            </p>
          </div>

          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Conversion Calculator
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Gallons (US):
                </label>
                <input
                  type="number"
                  className={`w-full px-4 py-3.5 border rounded-xl shadow-sm focus:outline-none focus:ring-2 transition-all input-green ${inputClass}`}
                  placeholder="Enter gallons"
                  value={gallons}
                  onChange={(e) => handleConvert(e.target.value)}
                />
              </div>

              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Liters:
                </label>
                <div className={`w-full px-4 py-3.5 border rounded-xl ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <span className={`text-xl font-bold ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                    {result ? `${result.liters} L` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {result && (
              <div className={`mt-6 p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-700 to-cyan-700' : 'bg-gradient-to-r from-blue-600 to-cyan-500'} text-white`}>
                <p className="text-center text-lg">
                  <span className="font-bold">{result.gallons}</span> gallons = <span className="font-bold">{result.liters}</span> liters
                </p>
                <p className="text-center text-sm mt-1 opacity-80">
                  (approximately {result.litersRounded} L)
                </p>
              </div>
            )}

            <div className={`mt-6 grid grid-cols-2 md:grid-cols-4 gap-3`}>
              {[1, 5, 10, 20].map((gal) => (
                <button
                  key={gal}
                  onClick={() => handleConvert(gal.toString())}
                  className={`p-3 rounded-lg text-center transition-all ${
                    darkMode
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                >
                  <div className="font-bold">{gal} gal</div>
                  <div className="text-sm opacity-80">{(gal * 3.78541).toFixed(1)} L</div>
                </button>
              ))}
            </div>
          </div>

          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Gallons to Liters Conversion Table
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700' : 'bg-blue-50'}>
                    <th className={`px-4 py-2 text-left ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Gallons</th>
                    <th className={`px-4 py-2 text-right ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Liters</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 5, 10, 15, 20, 25, 30, 50].map((gal) => (
                    <tr key={gal} className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                      <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{gal} gal</td>
                      <td className={`px-4 py-2 text-right font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {(gal * 3.78541).toFixed(2)} L
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-8">
            <FuelPriceTable darkMode={darkMode} dollarRate={dollarRate} lang="en" />
          </div>

          <SEOContent darkMode={darkMode} title="How to Convert Gallons to Liters?">
            <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Formula for Converting Gallons to Liters
            </h3>
            <p>
              Converting US gallons to liters is very simple. Just multiply the number of gallons by <strong>3.78541</strong>.
              For quick mental calculations, you can use the approximate value of 3.8 - the error will be minimal.
            </p>
            <p className="mt-3">
              <strong>Formula:</strong> Liters = Gallons × 3.78541
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              US Gallon vs Imperial Gallon
            </h3>
            <p>
              It's important to note that the US gallon differs from the Imperial gallon (UK gallon) used in Great Britain.
              A US gallon is 3.785 liters, while an Imperial gallon is about 4.546 liters. In the USA,
              only the US gallon is used, so you don't need to worry about this difference when traveling in America.
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Practical Applications
            </h3>
            <p>
              The gallons to liters converter is especially useful when:
            </p>
            <ul className={`list-disc list-inside mt-2 space-y-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <li>Planning a road trip in the USA</li>
              <li>Refueling a rental car</li>
              <li>Comparing fuel prices between the US and Europe</li>
              <li>Calculating road trip costs</li>
            </ul>
          </SEOContent>
        </div>
      </Layout>
    </>
  );
};

export default GallonsToLitersEN;
